// A REAL FALL, resolved in the browser — Janus Session A step 9 (2026-09-30). The twin of check_and_apply_falling.php's unsupported tail
// (:356-418) and apply_fall_damage_in_feet.php, for ONE creature whose fall the movement twin already measured (fall-check.ts predictFall:
// `{ kind: 'fall', fallFt }`):
//   1. the public `creature_fell` row (roll_result = round(fall), by the faller) when the fall is > 0.05 ft;
//   2. janus_trigger('triggers_when_caster_begins_falling') on the faller — any candidate hands back (not modelled yet);
//   3. resolve_negate_falling_damage_in_feet(): the row's rollup + active statuses ('inf' = nothing, a number subtracts, biggest wins);
//      a passive carrying the layer (runtime conditions) hands back;
//   4. effective ≤ 5 ft → nothing more; ≥ 10 ft → Prone (apply_prone_status: its own connection, the faller as caster, permanent);
//   5. dice = floor(ft / 10), die = [6, 8, 10, 12, 20][min(dice − 1, 4)], −2 dice for a hovering-preferred creature that can hover,
//      ≤ 0 dice → no roll; else the Falling Damage ability (BE42824F…) with those dice, dispatched through janus() — the victim as caster
//      and target — so resistances, damage reactions, temporary vitality and dying / death all run.
// The landing position is the MOVE's to write (the force-move commit lands the row); a grappler dropping its grapplee (PHP's recursion)
// hands back. Committed through ability_truth_commit_inc.php's `fall` block.
import { type JanusCtx, type Row, ecId, ecLean, handBack, insertRoll, itemField, record, scalar, upper, definitionNotHeld } from '../context.js';
import { janus, type JanusInput } from '../janus.js';
import { applyStatusEffect } from '../statuses/apply-status-effect.js';
import { collectTriggerCandidates } from '../../reactions/trigger-dispatch.js';
import { janusCtxTriggerReaders } from '../../reactions/janus-ctx-trigger-readers.js';
import { triggerCall } from '../trigger-executor.js';

export const FALLING_DAMAGE_ID = 'BE42824F-46BE-EC80-AB7F-B823190AB6C8';
const PRONE_ID = 'F5ACBDB4-D923-3684-57F6-FBF3F720AEFA';
const GRAPPLING_ID = 'E4A34093-AAAB-6453-BCE0-FBA65EE263AD';
const SPEED_HOVERING = 'AE7D4EB3-DC36-BD47-978F-1DBCFE2B616A'; // SpeedType speed_hovering
const BEGINS_FALLING = 'triggers_when_caster_begins_falling';
const NEGATE = 'negate_falling_damage_in_feet';
const DICE_BY_TIER = [6, 8, 10, 12, 20];

/** resolve_negate_falling_damage_in_feet(): Infinity for 'inf', else the biggest single number (0 when none). */
function fallNegation(ctx: JanusCtx, victim: Row): number {
  let best = 0;
  const scan = (entries: unknown[]): boolean => {
    for (const entry of entries) {
      const raw = scalar(record(entry)?.value ?? entry);
      if (typeof raw === 'string' && raw.trim().toLowerCase() === 'inf') return true;
      if (raw === null || raw === undefined || raw === '') continue;
      const n = Number(raw);
      if (Number.isFinite(n) && n > best) best = n;
    }
    return false;
  };
  if (scan(ctx.read.entries(victim, NEGATE))) return Infinity;
  // 1b: an ACTIVE passive carrying the layer is judged by check_ability_conditions() at the moment of the fall — not modelled
  for (const entry of ctx.read.entries(victim, 'passive_ability')) {
    const wrapper = record(entry);
    if (wrapper && 'passive_conditions_active' in wrapper && !wrapper.passive_conditions_active) continue;
    const def = record(wrapper?.item_arr) ?? ctx.read.definition(upper(typeof entry === 'string' ? entry : itemField(wrapper, 'item_id')));
    if (!def) definitionNotHeld('apply_fall_damage_in_feet:passive-definition-not-held', upper(typeof entry === 'string' ? entry : itemField(wrapper, 'item_id')));
    if (ctx.read.has(def, NEGATE)) handBack('apply_fall_damage_in_feet:passive-negation');
  }
  for (const status of ctx.read.statuses(victim)) {
    const def = record(status.status_arr) ?? ctx.read.definition(upper(status.status_item_id));
    if (!def) definitionNotHeld('apply_fall_damage_in_feet:status-definition-not-held', status.status_item_id);
    if (ctx.read.has(def, NEGATE) && scan(ctx.read.entries(def, NEGATE))) return Infinity;
  }
  return best;
}

export function resolveFallDamage(ctx: JanusCtx, input: JanusInput, fallingDamage: Row, victim: Row, fallFt: number): void {
  if (!Number.isFinite(fallFt) || fallFt < 0) handBack('check_and_apply_falling:fall-distance');
  const victimId = ecId(victim);
  if (!victimId) handBack('check_and_apply_falling:non-creature');
  if (fallFt <= 0.05) return;
  // PHP recurses into every creature the faller GRAPPLES (they fall with it) — not modelled
  if (ctx.read.statuses(victim).some((s) => upper(s.status_item_id ?? itemField(record(s.status_arr), 'item_id')) === GRAPPLING_ID))
    handBack('check_and_apply_falling:grappler-drops-grapplee');
  insertRoll(ctx, { roll_type_1: 'creature_fell', roll_type_2: '', by: victim, roll_result: Math.round(fallFt) });
  if (ctx.read.has(victim, BEGINS_FALLING)) handBack(`janus_trigger:unmodelled:trigger-on-row:${BEGINS_FALLING}`);
  if (!ctx.read.reactionInventoryComplete(victim)) handBack('check_and_apply_falling:reaction-inventory-incomplete');
  const { carriers, statuses } = triggerCall(() => collectTriggerCandidates(janusCtxTriggerReaders(ctx), victim, { trigger: BEGINS_FALLING }));
  if (carriers.length || statuses.length) handBack(`janus_trigger:unmodelled:${BEGINS_FALLING}`);
  const negate = fallNegation(ctx, victim);
  if (negate === Infinity) return;
  const effective = Math.max(0, fallFt - negate);
  if (effective <= 5) return;
  if (effective >= 10) {
    // apply_prone_status(): status_connected_id '' (its own connection), the faller as caster and target, status_ends 0
    applyStatusEffect(ctx, { ...input, abilityId: PRONE_ID } /* status_origin_id '' */, fallingDamage, PRONE_ID, 'status_effect_applied', victim, victim, { ends: '0-0-0-0-0-0', flags: [0, 0, 0, 0] }, true);
    const tier = Math.floor(effective / 10);
    const die = DICE_BY_TIER[Math.min(tier - 1, 4)]!;
    let dice = tier;
    const preferred = upper(ecLean(victim, 'preferred_speed_type'));
    if (preferred === SPEED_HOVERING && Number(scalar(ctx.read.value(victim, 'speed_hovering')) ?? 0) > 0) dice -= 2;
    if (dice <= 0) return;
    const damage = { ...fallingDamage, item_layer_types: { ...(record(fallingDamage.item_layer_types) ?? {}), dice, die } };
    janus(ctx, { ...input, casterId: victimId, targetIds: [victimId], abilityId: FALLING_DAMAGE_ID, castingTargets: victimId,
      useAbilityVariable1: null, useAbilityVariable2: null }, damage, victim, [victim]);
  }
}
